import { useRef, useState } from "react";
import {
    motion,
    useMotionValue,
    useSpring,
    useReducedMotion,
} from "framer-motion";

const REGIONS = [
    {
        id: "asean",
        label: "ASEAN Region",
        countries:
            "Malaysia · Singapore · Indonesia · Thailand · Vietnam · Philippines",
    },
    {
        id: "china",
        label: "Greater China",
        countries: "China · Hong Kong",
    },
    {
        id: "me",
        label: "Middle East",
        countries:
            "United Arab Emirates · Saudi Arabia · Qatar · Oman · Bahrain · Turkey · Iran · Lebanon",
    },
    {
        id: "oceania",
        label: "Oceania",
        countries: "Australia · New Zealand",
    },
    {
        id: "na",
        label: "North America",
        countries: "Canada",
    },
];

export const Markets = () => {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const [hovered, setHovered] = useState(null);
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);
    const springX = useSpring(mouseX, { stiffness: 250, damping: 30 });
    const springY = useSpring(mouseY, { stiffness: 250, damping: 30 });

    const onMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        mouseX.set(e.clientX - rect.left);
        mouseY.set(e.clientY - rect.top);
    };

    return (
        <section
            ref={ref}
            id="markets"
            data-testid="markets-section"
            onMouseMove={reduce ? undefined : onMouseMove}
            className="relative py-24 sm:py-32 lg:py-40 px-4 sm:px-8 lg:px-12 border-t border-white/5 overflow-hidden custom-cursor-area cursor-auto lg:cursor-none"
            style={{
                background:
                    "radial-gradient(ellipse 80% 100% at 50% 50%, rgba(5,5,5,0.4) 0%, rgba(5,5,5,0.92) 75%)",
            }}
        >
            {/* Cursor blob */}
            {!reduce && (
                <motion.div
                    aria-hidden
                    style={{
                        x: springX,
                        y: springY,
                        translateX: "-50%",
                        translateY: "-50%",
                    }}
                    className="hidden lg:flex absolute top-0 left-0 z-30 pointer-events-none items-center justify-center"
                >
                    <motion.div
                        animate={{
                            scale: hovered ? 1 : 0.6,
                            opacity: hovered ? 1 : 0.5,
                        }}
                        transition={{ duration: 0.3 }}
                        className="w-24 h-24 rounded-full bg-[#D69331] mix-blend-difference"
                    />
                    {hovered && (
                        <span className="absolute font-mono-text text-[10px] uppercase tracking-[0.3em] text-black">
                            View
                        </span>
                    )}
                </motion.div>
            )}

            {/* Decorative rotating orb handled by 3D scene */}

            <div className="relative z-10">
                <div className="flex items-center gap-3 font-mono-text text-[10px] sm:text-xs uppercase tracking-[0.3em] text-zinc-500 mb-6">
                    <span className="h-px w-10 bg-[#D69331]" />
                    <span>05 / Global Market Coverage</span>
                </div>

                <motion.h2
                    initial={{ y: 60, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8 }}
                    className="font-display uppercase font-bold leading-[0.9] tracking-tight text-white text-4xl sm:text-6xl lg:text-7xl max-w-5xl"
                >
                    Connecting businesses across{" "}
                    <span className="text-zinc-600">
                        strategic international markets.
                    </span>
                </motion.h2>

                <p className="mt-8 font-mono-text text-sm sm:text-base text-zinc-400 leading-relaxed max-w-2xl">
                    DUHA Global Trading is committed to developing and
                    strengthening commercial relationships across some of the
                    world's most important trading regions.
                </p>

                <ul className="mt-16 divide-y divide-white/10 border-t border-b border-white/10">
                    {REGIONS.map((r, i) => (
                        <li
                            key={r.id}
                            onMouseEnter={() => setHovered(r.id)}
                            onMouseLeave={() => setHovered(null)}
                            className="group"
                        >
                            <a
                                href="#contact"
                                data-testid={`market-${r.id}`}
                                className="grid grid-cols-12 gap-4 py-6 sm:py-10 items-center"
                            >
                                <span className="col-span-2 sm:col-span-1 font-mono-text text-[10px] uppercase tracking-[0.3em] text-zinc-600">
                                    0{i + 1}
                                </span>
                                <span
                                    className={`col-span-10 sm:col-span-4 font-display uppercase text-2xl sm:text-3xl lg:text-5xl font-semibold leading-tight transition-colors ${
                                        hovered === r.id
                                            ? "text-[#D69331]"
                                            : "text-white"
                                    }`}
                                >
                                    {r.label}
                                </span>
                                <span className="col-span-12 sm:col-span-7 font-mono-text text-xs sm:text-sm text-zinc-400 leading-relaxed">
                                    {r.countries}
                                </span>
                            </a>
                        </li>
                    ))}
                </ul>

                <p className="mt-12 font-mono-text text-sm sm:text-base text-zinc-400 max-w-3xl leading-relaxed">
                    Our vision extends beyond individual transactions. We are
                    building an interconnected global trade ecosystem that
                    creates opportunities for manufacturers, distributors,
                    retailers and consumers through{" "}
                    <span className="text-white">sustainable and efficient</span>{" "}
                    international commerce.
                </p>
            </div>
        </section>
    );
};
